/**
 * Que tema cada rota usa quando ela NÃO deixa a escolha para quem abre.
 *
 * A regra geral do app é o ESCURO: ele é o padrão para os dois lados (personal
 * e aluno), e a partir da primeira troca no cabeçalho ou nas configurações vale
 * a escolha da pessoa, guardada no aparelho dela (ver `app/layout.tsx`). Três
 * famílias de rota ficam fora dessa regra, por motivos diferentes.
 *
 * **1. Página externa é sempre clara.** É o link que o personal manda para
 * outra pessoa: o horário para o aluno marcar, o cadastro de quem está
 * entrando, a avaliação física para mostrar o resultado, a cobrança. Ali o tema
 * do aparelho de quem abre não tem nada a ver com o assunto: o link é a cara do
 * personal na frente do cliente dele, e precisa sair igual para todo mundo, no
 * celular do aluno e no computador da recepção. Claro porque é como o produto
 * nasce.
 *
 * **2. Entrar e criar conta são sempre ESCUROS.** É a porta do produto, e a
 * porta é a mesma para todo mundo: quem vê a tela de entrar pela primeira vez
 * não tem escolha de tema guardada, e quem já usa o app volta para cá ao sair
 * da conta. Ela já acompanhou o tema do aparelho, e o resultado era a porta
 * mudando de cor conforme quem chega; depois foi sempre clara, e a foto de
 * academia dentro de uma folha branca era a vitrine brigando com a moldura.
 *
 * Escura porque a porta é a FOTO: a academia em preto e branco é o que a tela
 * mostra antes de qualquer texto, e ela pede o fundo escuro em volta, não uma
 * borda branca. É a mesma decisão da casca do painel, que no tema claro sai
 * preta: chão e teto do produto são escuros, e o claro é do conteúdo.
 *
 * **3. Tela ainda não migrada continua escura.** São telas do tema antigo, que
 * nasceram para fundo escuro. Com o escuro virando o padrão do app, a lista
 * deixou de mudar o que aparece na tela e passou a ser só o inventário do que
 * falta migrar: ela sai daqui conforme cada tela for redesenhada.
 *
 * **Por que uma lista de rota, e não uma prop em cada tela:** o tema tem que
 * estar decidido ANTES da pintura, no script que o `next-themes` roda no
 * `<head>`. Tela que decide o tema por dentro só decide depois de montar, e quem
 * abre vê o tema errado por um quadro (foi o flash branco que a avaliação
 * compartilhada dava ao forçar claro com `setTheme`, que ainda por cima mexia na
 * preferência guardada do aparelho).
 */

export type TemaForcado = "light" | "dark"

/**
 * Páginas externas: o link que sai da conta do personal para outra pessoa.
 *
 * `/p/` cobre a página pública e o agendamento dela, e `/convite` é o cadastro
 * do aluno pelo link do personal. Ela NÃO acompanhou a porta para o escuro: quem
 * abre esse link não está entrando no Welltrainer, está respondendo ao convite de
 * um personal, e ali a tela é a cara dele.
 */
const EXTERNAS_CLARAS = [
  /* A avaliação física compartilhada por link. */
  "/a/",
  /* Página pública do personal e o agendamento dela. As duas pintam o próprio
     fundo com as cores que o personal escolheu no construtor; o claro aqui é o
     do documento por baixo, que é o que aparece na barra de rolagem e no
     esticar da rolagem. */
  "/p/",
  "/convite",
  /* Cobrança e venda: quem abre é quem vai pagar. */
  "/pagar",
  /* O checkout de quem chega pela página de vendas. Ele é a CONTINUAÇÃO da
     landing, que é branca: a pessoa toca em "Assinar Pro" numa página clara e
     caía numa tela preta, e a troca de cor no meio da compra parecia outro
     site. O tema do aparelho não decide aqui pelo mesmo motivo das outras
     externas: quem abre ainda não é do app, e esta tela é a nossa vitrine. */
  "/planos",
  /* `/pagar-pix` saiu com a rota: o Pix da assinatura é sempre por mandato, e não
     existe mais a página pública que gerava um QR por mês. */
  "/produto",
  /* Conferência de documento por quem recebeu o link. */
  "/kyc-review",
  /* O contrato entre o personal e o aluno aberto pelo link público, e a
     página de conferir um contrato pelo código ou pelo PDF. Quem abre quase
     nunca é do app: é quem uma das partes mandou conferir. */
  "/contrato/",
  "/verificar",
]

/**
 * A porta do produto: entrar, criar conta, ativar e recuperar a senha.
 *
 * É a lista da [`CascaDeEntrada`](../../components/auth/casca-de-entrada.tsx),
 * mais `/reset-password`, que é a segunda metade do "esqueci a senha" e por isso
 * anda junto: pedir o código numa tela escura e digitar a senha nova numa clara
 * é degrau no meio do caminho.
 *
 * `/ativar-conta` e `/criar-senha` chegam por link de fora, mas desenham na
 * casca de entrada e por isso moram AQUI, e não com as externas: a primeira é a
 * porta do aluno convidado e a segunda é a de quem assinou pela página de vendas
 * e ainda não tem senha. Nos dois casos quem está do outro lado é o dono da
 * conta, e a porta é a mesma.
 *
 * `/convite` continua clara porque ali quem se apresenta é o PERSONAL: é a
 * página dele na frente de um aluno que ainda nem tem conta.
 */
const ENTRADA_ESCURA = [
  "/login",
  /* A volta do "Entrar com Apple" no celular (16/10/2026): a mesma tela de
     entrar, em outro endereço para escapar do link universal do `/login` (ver
     `app/entrar/apple/page.tsx`). */
  "/entrar",
  "/signup",
  /* O fim do cadastro é a última tela da porta, e não a primeira do painel: ela
     desenha na mesma coluna estreita, logo depois de `/signup` ou de "Falta
     pouco", e mudar de cor no caminho seria um degrau no meio da entrada.
     `/pagamento-concluido` fica de fora de propósito: quem chega lá em geral já
     está dentro do app, e ali vale a escolha de tema da pessoa. */
  "/cadastro-concluido",
  "/forgot-password",
  "/reset-password",
  "/completar-cadastro",
  "/ativar-conta",
  "/criar-senha",
  /* A tela de permissão que o Claude ou o ChatGPT abrem no navegador
     (15/10/2026). Ela desenha na casca de entrada porque é a continuação do
     login: quem não estava logado passa por `/login` e volta para cá, e mudar de
     cor entre as duas seria o degrau no meio da porta. */
  "/conectar-ia",
]

/**
 * Telas que continuam escuras.
 *
 * `/indicar` é escura por IDENTIDADE, e não por falta de migração: ela é a
 * landing de indicação, da mesma família da home (`app/welcome.tsx`, preta), e
 * não uma tela do app. As outras duas são do tema antigo e ficam onde estão até
 * serem redesenhadas: mudar o tema delas junto com esta troca seria mexer no
 * visual de tela que ninguém conferiu.
 */
const LEGADO_ESCURO = ["/indicar", "/bem-vindo", "/conta-bloqueada"]

function rotaCasa(pathname: string, prefixo: string): boolean {
  if (pathname === prefixo) return true
  const comBarra = prefixo.endsWith("/") ? prefixo : prefixo + "/"
  return pathname.startsWith(comBarra)
}

/** O tema desta rota, ou `undefined` quando ela segue a preferência da pessoa. */
export function temaForcadoPorRota(pathname: string): TemaForcado | undefined {
  if (ENTRADA_ESCURA.some((p) => rotaCasa(pathname, p))) return "dark"
  if (EXTERNAS_CLARAS.some((p) => rotaCasa(pathname, p))) return "light"
  if (LEGADO_ESCURO.some((p) => rotaCasa(pathname, p))) return "dark"
  return undefined
}
